"use client"

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../ui/card"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { useMemo, useState } from "react"
import { Toast } from "../../Toast"
import {
  submitVolunteerForm,
  deleteVolunteerForm,
} from "../../../api/adminService"
import { exportToCSV } from "../../utils/exportToCSV"

const ITEMS_PER_PAGE = 5

export function VolunteersFormSection({
  volunteersFormList = [],
  setVolunteersFormList,
}: any) {
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const filteredList = useMemo(() => {
    return volunteersFormList.filter((item: any) =>
      item.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.city_country?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [volunteersFormList, searchTerm])

  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE)
  const paginatedItems = filteredList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleDelete = async (id: string | number) => {
    try {
      await deleteVolunteerForm(id)
      setVolunteersFormList((prev: any) =>
        prev.filter((i: any) => i.id !== id)
      )
      Toast.success("Volunteer form deleted")
    } catch {
      Toast.error("Delete failed")
    }
  }

  const handleDownloadCSV = () => {
    if (volunteersFormList.length === 0) {
      Toast.error("No data to export")
      return
    }

    exportToCSV(volunteersFormList, "volunteer-forms.csv")
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center gap-2">
          <div>
            <CardTitle>🙋 Volunteers Forms</CardTitle>
            <CardDescription>
              Manage volunteer applications
            </CardDescription>
          </div>

          <Button size="sm" onClick={handleDownloadCSV}>
            Download CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Input
          placeholder="Search by name, email, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {paginatedItems.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            No records found
          </p>
        ) : (
          paginatedItems.map((item: any) => (
            <div key={item.id} className="border rounded p-4 space-y-1">
              <p className="font-semibold">{item.full_name}</p>

              <p className="text-sm text-muted-foreground">
                {item.email} | {item.phone}
              </p>

              <p className="text-sm">
                {item.city_country} • Interested in:{" "}
                {item.department_interests}
              </p>

              <p className="text-xs text-muted-foreground">
                Submitted on {item.date_created}
              </p>

              {/* Optional delete button */}
              {/* 
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(item.id)}
              >
                Delete
              </Button>
              */}
            </div>
          ))
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(currentPage * ITEMS_PER_PAGE, filteredList.length)} of{" "}
            {filteredList.length}
          </p>

          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}